import {
  groundSingleWitnessFocalizationCharacterization,
  groundSingleWitnessCoreferenceChainsV2,
  projectGroundedCoreferenceChainsV2ToSourceAnchors,
  validateSingleWitnessFocalizationCharacterizationOutput,
  validateSingleWitnessCoreferenceChainV2Output,
  type GroundedSingleWitnessCoreferenceChainV2Mention,
  type PreparedSingleWitnessFocalizationCharacterizationExtractionCall,
  type PreparedSingleWitnessCoreferenceChainV2ExtractionCall,
  type SingleWitnessFocalizationCharacterizationOutput,
  type SingleWitnessCoreferenceChainV2Figure,
  type SingleWitnessCoreferenceChainV2Output,
} from '@variorum/character-index'
import type {
  ChapterCharacterLayer,
  CharacterEntity,
  CharacterLayerDetectorOutput,
  CharacterLayerNote,
  CharacterOccurrence,
} from '../types/characterLayerTypes'

export const PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT = 'package-coreference-chain-v2'
export const PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT = 'package-focalization-characterization-v1'

type PreparedV2Call = Pick<PreparedSingleWitnessCoreferenceChainV2ExtractionCall, 'sourceText' | 'sourceMap'>
type PreparedFocalizationCharacterizationCall = Pick<
  PreparedSingleWitnessFocalizationCharacterizationExtractionCall,
  'figures' | 'focusFigureId' | 'sourceText' | 'sourceMap'
>

type FocalizationCharacterizationSource =
  | { voice: 'narrator'; level?: number; narrator?: string }
  | { voice: 'figure'; figure?: string; channel?: 'speech' | 'interior' | string }
  | { voice: 'communal' }
  | Record<string, unknown>

const normalizeText = (value: unknown): string => (
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : ''
)

const stableHash = (value: string): string => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
)

const isStringArray = (value: unknown): value is string[] => (
  Array.isArray(value) && value.every(item => typeof item === 'string')
)

const getSourceAnchorLookupKey = ({
  localEnd,
  localStart,
  mentionId,
  rowId,
  witnessId,
}: {
  witnessId: string
  rowId: string
  mentionId: string
  localStart?: number | null
  localEnd?: number | null
}): string => [
  witnessId,
  rowId,
  mentionId,
  typeof localStart === 'number' && Number.isFinite(localStart) ? localStart : '',
  typeof localEnd === 'number' && Number.isFinite(localEnd) ? localEnd : '',
].join('::')

const isValidCoreferenceV2Output = (
  value: unknown,
): value is SingleWitnessCoreferenceChainV2Output => (
  validateSingleWitnessCoreferenceChainV2Output(value).length === 0
)

const isValidFocalizationCharacterizationOutput = (
  value: unknown,
): value is SingleWitnessFocalizationCharacterizationOutput => (
  validateSingleWitnessFocalizationCharacterizationOutput(value).length === 0
)

const buildCoreferenceV2CharacterId = ({
  chapterId,
  figure,
}: {
  chapterId: string
  figure: SingleWitnessCoreferenceChainV2Figure
}): string => (
  `character-coref-v2-${stableHash([
    chapterId,
    figure.id,
    figure.kind,
    figure.anchor.name,
    figure.chain[0]?.context || '',
  ].join(':'))}`
)

const buildCoreferenceV2OccurrenceId = ({
  chapterId,
  characterId,
  mention,
  rowId,
  witnessId,
  localStart,
  localEnd,
}: {
  chapterId: string
  characterId: string
  mention: GroundedSingleWitnessCoreferenceChainV2Mention
  witnessId: string
  rowId: string
  localStart?: number
  localEnd?: number
}): string => (
  `character-occ-coref-v2-${stableHash([
    chapterId,
    characterId,
    mention.id,
    witnessId,
    rowId,
    localStart ?? '',
    localEnd ?? '',
    mention.mention,
  ].join(':'))}`
)

export const adaptPackageCoreferenceChainV2ToDetectorOutput = ({
  chapterId,
  createdAt,
  preparedCall,
  rawExtraction,
}: {
  rawExtraction: unknown
  preparedCall: PreparedV2Call
  chapterId: string
  createdAt?: string
}): CharacterLayerDetectorOutput => {
  const timestamp = createdAt || new Date().toISOString()
  const validationIssues = validateSingleWitnessCoreferenceChainV2Output(rawExtraction)
  if (!isValidCoreferenceV2Output(rawExtraction)) {
    return {
      characters: [],
      occurrences: [],
      notes: [],
      metadata: {
        output_contract: PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT,
        validation_issues: validationIssues,
      },
    }
  }

  const grounded = groundSingleWitnessCoreferenceChainsV2(preparedCall, rawExtraction)
  const projectedMentionTypeByAnchorKey = new Map(
    projectGroundedCoreferenceChainsV2ToSourceAnchors(grounded).map(anchor => [
      getSourceAnchorLookupKey(anchor),
      anchor.mentionType,
    ])
  )
  const characters: CharacterEntity[] = []
  const occurrences: CharacterOccurrence[] = []

  grounded.figures.forEach(figure => {
    const groundedMentions = figure.chain.filter(mention => mention.status === 'grounded')
    if (groundedMentions.length === 0) {
      return
    }
    const characterId = buildCoreferenceV2CharacterId({ chapterId, figure })
    characters.push({
      character_id: characterId,
      kind: figure.kind,
      display_name: normalizeText(figure.anchor.name) || `[${figure.id}]`,
      aliases: [],
      status: 'auto',
      origin: 'llm',
      metadata: {
        output_contract: PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT,
        coreference_contract_version: 2,
        figure_id: figure.id,
        figure_kind: figure.kind,
        chain_anchor: figure.anchor.name,
        transliteration: figure.anchor.transliteration,
        coreference_chain_size: figure.chain.length,
      },
      created_at: timestamp,
      updated_at: timestamp,
    })

    groundedMentions.forEach(mention => {
      mention.sourceAnchors.forEach(anchor => {
        const localStart = typeof anchor.localStart === 'number' ? anchor.localStart : undefined
        const localEnd = typeof anchor.localEnd === 'number' ? anchor.localEnd : undefined
        occurrences.push({
          occurrence_id: buildCoreferenceV2OccurrenceId({
            chapterId,
            characterId,
            mention,
            witnessId: anchor.witnessId,
            rowId: anchor.rowId,
            localStart,
            localEnd,
          }),
          character_id: characterId,
          locator: {
            witnessId: anchor.witnessId,
            rowId: anchor.rowId,
            ...(typeof localStart === 'number' ? { localStart } : {}),
            ...(typeof localEnd === 'number' ? { localEnd } : {}),
          },
          role_labels: ['mentioned'],
          label: mention.mention,
          summary: mention.context,
          status: 'auto',
          origin: 'llm',
          metadata: {
            output_contract: PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT,
            coreference_contract_version: 2,
            figure_id: figure.id,
            figure_kind: figure.kind,
            mention_id: mention.id,
            mention_type: projectedMentionTypeByAnchorKey.get(getSourceAnchorLookupKey({
              witnessId: anchor.witnessId,
              rowId: anchor.rowId,
              mentionId: mention.id,
              localStart,
              localEnd,
            })) || 'coref',
            mention: mention.mention,
            context: mention.context,
            ...(mention.matchedText ? { matched_text: mention.matchedText } : {}),
            ...(isStringArray(mention.with) && mention.with.length ? { with: mention.with } : {}),
            ...(anchor.window ? { anchor_window: anchor.window } : {}),
            ...(anchor.unitUuid ? { unitUuid: anchor.unitUuid } : {}),
            ...(anchor.unitLabel ? { unitLabel: anchor.unitLabel } : {}),
            ...(anchor.unitId ? { unitId: anchor.unitId } : {}),
          },
          created_at: timestamp,
          updated_at: timestamp,
        })
      })
    })
  })

  return {
    characters,
    occurrences,
    notes: [],
    metadata: {
      output_contract: PACKAGE_COREFERENCE_CHAIN_V2_OUTPUT_CONTRACT,
      coreference_contract_version: 2,
      figure_count: rawExtraction.figures.length,
      occurrence_count: occurrences.length,
      unassigned_mention_count: rawExtraction.unassigned_mentions.length,
      grounding_diagnostic_count: grounded.diagnostics.length,
      grounding_diagnostics: grounded.diagnostics,
      validation_issues: [],
      grounding: grounded,
    },
  }
}

const getFigureIds = (value: string | readonly string[] | undefined): string[] => (
  Array.isArray(value) ? value.filter(Boolean) : value ? [value] : []
)

const formatSource = (source: FocalizationCharacterizationSource | undefined): string => {
  const record = asRecord(source)
  if (!record) {
    return 'source unknown'
  }
  if (record.voice === 'figure') {
    return `figure ${String(record.figure || 'unknown')}${record.channel ? `/${String(record.channel)}` : ''}`
  }
  if (record.voice === 'narrator') {
    return `narrator N${String(record.level ?? '?')}${record.narrator ? `/${String(record.narrator)}` : ''}`
  }
  if (record.voice === 'communal') {
    return 'communal'
  }
  return String(record.voice || 'source unknown')
}

const buildFigureToCharacterIdMap = (layer?: ChapterCharacterLayer): Map<string, string> => {
  const map = new Map<string, string>()
  ;(layer?.characters || []).forEach(character => {
    map.set(character.character_id, character.character_id)
    const metadata = character.metadata || {}
    const figureIds = [
      metadata.figure_id,
      metadata.package_figure_id,
      metadata.coreference_v2_figure_id,
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    figureIds.forEach(figureId => map.set(figureId, character.character_id))
  })
  return map
}

const buildLayeredNoteId = ({
  characterId,
  layer,
  sourceId,
  text,
}: {
  characterId: string
  layer: string
  sourceId: string
  text: string
}): string => (
  `character-note-${layer}-${stableHash([characterId, sourceId, text].join(':'))}`
)

const isFiniteSourceAnchorSpan = <T extends { localStart?: number | null; localEnd?: number | null }>(
  anchor: T
): anchor is T & { localStart: number; localEnd: number } => (
  typeof anchor.localStart === 'number' &&
  typeof anchor.localEnd === 'number' &&
  Number.isFinite(anchor.localStart) &&
  Number.isFinite(anchor.localEnd) &&
  anchor.localEnd > anchor.localStart
)

const buildLayeredSpanNoteId = ({
  anchor,
  characterId,
  layer,
  sourceField,
  sourceId,
  text,
}: {
  characterId: string
  layer: 'focalization' | 'characterization'
  sourceId: string
  sourceField: string
  text: string
  anchor: { witnessId: string; rowId: string; localStart?: number | null; localEnd?: number | null }
}): string => (
  `character-note-${layer}-span-${stableHash([
    characterId,
    sourceId,
    sourceField,
    anchor.witnessId,
    anchor.rowId,
    anchor.localStart ?? '',
    anchor.localEnd ?? '',
    text,
  ].join(':'))}`
)

export const adaptPackageFocalizationCharacterizationToDetectorOutput = ({
  createdAt,
  existingLayer,
  preparedCall,
  rawExtraction,
}: {
  rawExtraction: unknown
  preparedCall: PreparedFocalizationCharacterizationCall
  existingLayer?: ChapterCharacterLayer
  createdAt?: string
}): CharacterLayerDetectorOutput => {
  const timestamp = createdAt || new Date().toISOString()
  const validationIssues = validateSingleWitnessFocalizationCharacterizationOutput(rawExtraction)
  if (!isValidFocalizationCharacterizationOutput(rawExtraction)) {
    return {
      characters: [],
      occurrences: [],
      notes: [],
      metadata: {
        output_contract: PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT,
        validation_issues: validationIssues,
      },
    }
  }

  const grounded = groundSingleWitnessFocalizationCharacterization(preparedCall, rawExtraction)
  const figureToCharacterId = buildFigureToCharacterIdMap(existingLayer)
  const focusFigureId = normalizeText(preparedCall.focusFigureId)
  const focusCharacterId = focusFigureId ? figureToCharacterId.get(focusFigureId) || focusFigureId : ''
  const notes: CharacterLayerNote[] = []
  const unmatchedFigureIds = new Set<string>()

  grounded.focalization.forEach(item => {
    getFigureIds(item.experiencer).forEach(figureId => {
      const characterId = figureToCharacterId.get(figureId)
      if (!characterId) {
        unmatchedFigureIds.add(figureId)
        return
      }
      const text = [
        `Focalization ${item.id || ''}`.trim(),
        item.class ? `[${item.class}]` : '',
        item.marker ? `marker: ${item.marker}` : '',
        item.scope ? `scope: ${item.scope}` : '',
        item.context ? `context: ${item.context}` : '',
        `source: ${formatSource(item.source)}`,
      ].filter(Boolean).join(' · ')
      notes.push({
        note_id: buildLayeredNoteId({ characterId, layer: 'focalization', sourceId: item.id, text }),
        anchor: { kind: 'character', characterId },
        text,
        created_at: timestamp,
        updated_at: timestamp,
      })
      const spanAnchors = item.markerSourceAnchors.length > 0 ? item.markerSourceAnchors : item.scopeSourceAnchors
      const sourceField = item.markerSourceAnchors.length > 0 ? 'marker' : 'scope'
      spanAnchors.filter(isFiniteSourceAnchorSpan).forEach(anchor => {
        notes.push({
          note_id: buildLayeredSpanNoteId({
            anchor,
            characterId,
            layer: 'focalization',
            sourceField,
            sourceId: item.id,
            text,
          }),
          anchor: {
            kind: 'span',
            locator: {
              witnessId: anchor.witnessId,
              rowId: anchor.rowId,
              localStart: anchor.localStart,
              localEnd: anchor.localEnd,
            },
          },
          text,
          metadata: {
            output_contract: PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT,
            character_id: characterId,
            character_index_layer: 'focalization',
            source_id: item.id,
            source_field: sourceField,
            class: item.class,
            marker: item.marker,
            scope: item.scope,
            context: item.context,
            source: item.source,
            status: item.status,
          },
          created_at: timestamp,
          updated_at: timestamp,
        })
      })
    })
  })

  grounded.characterizations.forEach(item => {
    item.targets.forEach(figureId => {
      const characterId = figureToCharacterId.get(figureId)
      if (!characterId) {
        unmatchedFigureIds.add(figureId)
        return
      }
      const text = [
        `Characterization ${item.id || ''}`.trim(),
        item.devices.length ? `[${item.devices.join(', ')}]` : '',
        item.span ? `span: ${item.span}` : '',
        item.context ? `context: ${item.context}` : '',
        item.mention_ref ? `mention: ${item.mention_ref}` : '',
        `source: ${formatSource(item.source)}`,
      ].filter(Boolean).join(' · ')
      notes.push({
        note_id: buildLayeredNoteId({ characterId, layer: 'characterization', sourceId: item.id, text }),
        anchor: { kind: 'character', characterId },
        text,
        created_at: timestamp,
        updated_at: timestamp,
      })
      item.spanSourceAnchors.filter(isFiniteSourceAnchorSpan).forEach(anchor => {
        notes.push({
          note_id: buildLayeredSpanNoteId({
            anchor,
            characterId,
            layer: 'characterization',
            sourceField: 'span',
            sourceId: item.id,
            text,
          }),
          anchor: {
            kind: 'span',
            locator: {
              witnessId: anchor.witnessId,
              rowId: anchor.rowId,
              localStart: anchor.localStart,
              localEnd: anchor.localEnd,
            },
          },
          text,
          metadata: {
            output_contract: PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT,
            character_id: characterId,
            character_index_layer: 'characterization',
            source_id: item.id,
            source_field: 'span',
            devices: item.devices,
            span: item.span,
            context: item.context,
            mention_ref: item.mention_ref,
            source: item.source,
            status: item.status,
          },
          created_at: timestamp,
          updated_at: timestamp,
        })
      })
    })
  })

  const outputNotes = focusCharacterId
    ? notes.filter(note => {
      if (note.anchor.kind === 'character') {
        return note.anchor.characterId === focusCharacterId
      }
      const metadata = asRecord(note.metadata)
      return metadata?.character_id === focusCharacterId
    })
    : notes
  const discardedNonFocusNoteCount = Math.max(0, notes.length - outputNotes.length)

  return {
    characters: [],
    occurrences: [],
    notes: outputNotes,
    metadata: {
      output_contract: PACKAGE_FOCALIZATION_CHARACTERIZATION_OUTPUT_CONTRACT,
      focalization_count: rawExtraction.focalization.length,
      characterization_count: rawExtraction.characterizations.length,
      unmatched_count: rawExtraction.unmatched.length,
      grounded_focalization_count: grounded.focalization.filter(record => record.status === 'grounded').length,
      grounded_characterization_count: grounded.characterizations.filter(record => record.status === 'grounded').length,
      grounding_diagnostic_count: grounded.diagnostics.length,
      grounding_diagnostics: grounded.diagnostics,
      generated_note_count: outputNotes.length,
      ...(focusFigureId ? {
        focus_figure_id: focusFigureId,
        focus_character_id: focusCharacterId,
        discarded_non_focus_note_count: discardedNonFocusNoteCount,
      } : {}),
      unmatched_figure_ids: Array.from(unmatchedFigureIds),
      validation_issues: [],
      grounding: grounded,
    },
  }
}
